// Email Delivery Log — extracted from the old BranchReports Emails tab
// so the audit trail is still reachable from the new directory (filed
// under Accounting since it's cross-functional and audit-flavoured).
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Mail, CheckCircle2, XCircle } from "lucide-react";
import ReportShell from "./ReportShell";

const TRIGGER_LABELS = {
  after_booking: "After booking",
  one_day_before_appt: "1 day before appt",
  three_days_before_appt: "3 days before appt",
  after_payment: "After payment",
  two_weeks_after_paid: "2 weeks after paid",
  three_months_after_paid: "3 months after paid",
  client_birthday: "Client birthday",
  one_month_before_membership_expiry: "1 month · membership expiry",
  one_month_before_series_expiry: "1 month · series expiry",
  one_week_before_series_expiry: "1 week · series expiry",
  after_cancel: "After cancel",
  after_no_show: "After no-show",
};

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function EmailLogReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [rows, setRows] = useState([]);
  const [triggerFilter, setTriggerFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (triggerFilter !== "__all__") params.trigger = triggerFilter;
      if (statusFilter !== "__all__") params.status = statusFilter;
      const r = await staffApi.get(`/branches/${bid}/email-dispatch-log`, { params });
      setRows(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [bid, triggerFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const sentCount = rows.filter((r) => r.ok).length;
  const failedCount = rows.length - sentCount;

  // Exports include the currently-applied trigger + status filters
  // so what you download == what you see.
  const exportSpec = useMemo(() => {
    if (!rows) return null;
    const exportRows = rows.map((r) => ({
      when: fmtDT(r.at),
      template: r.template_name || "(deleted)",
      trigger: TRIGGER_LABELS[r.trigger] || r.trigger,
      recipient: r.to || "(no email on file)",
      target: `${r.target_type}: ${String(r.target_id || "").slice(0, 8)}`,
      status: r.ok ? "Sent" : "Failed",
    }));
    return {
      filename: "email-delivery-log",
      range: null,
      subtitle: `Filters — trigger: ${triggerFilter === "__all__" ? "All" : (TRIGGER_LABELS[triggerFilter] || triggerFilter)} · status: ${statusFilter === "__all__" ? "All" : statusFilter} · ${sentCount} sent / ${failedCount} failed`,
      columns: [
        { header: "When",      key: "when" },
        { header: "Template",  key: "template" },
        { header: "Trigger",   key: "trigger" },
        { header: "Recipient", key: "recipient" },
        { header: "Target",    key: "target" },
        { header: "Status",    key: "status" },
      ],
      rows: exportRows,
    };
  }, [rows, triggerFilter, statusFilter, sentCount, failedCount]);

  return (
    <ReportShell title="Email Delivery Log" subtitle="Every automated email that fired for this branch, sent or failed. Configure RESEND_API_KEY to unblock delivery." branchName={branch?.name} tab="accounting" exportSpec={exportSpec}>
      <Card className="rounded-lg" data-testid="email-log-card">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 mr-auto">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Email Delivery Log</span>
              <Badge variant="outline" className="rounded-full text-[10px] gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" />{sentCount} sent</Badge>
              <Badge variant="outline" className="rounded-full text-[10px] gap-1"><XCircle className="w-3 h-3 text-rose-600" />{failedCount} failed</Badge>
            </div>
            <Select value={triggerFilter} onValueChange={setTriggerFilter}>
              <SelectTrigger className="w-56" data-testid="log-trigger-filter"><SelectValue placeholder="All triggers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All triggers</SelectItem>
                {Object.entries(TRIGGER_LABELS).map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="log-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                <SelectItem value="sent">Sent only</SelectItem>
                <SelectItem value="failed">Failed only</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1" data-testid="log-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="uppercase text-[10px] tracking-wider">When</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Template</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Trigger</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Recipient</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Target</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                  {loading ? "Loading…" : "No emails have been dispatched yet."}
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`email-log-row-${r.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{fmtDT(r.at)}</TableCell>
                  <TableCell className="text-sm">{r.template_name || <span className="text-muted-foreground italic">Deleted</span>}</TableCell>
                  <TableCell className="text-xs">{TRIGGER_LABELS[r.trigger] || r.trigger}</TableCell>
                  <TableCell className="text-xs">{r.to || <span className="text-muted-foreground italic">(no email on file)</span>}</TableCell>
                  <TableCell className="text-[10px] font-mono text-muted-foreground">{r.target_type}: {String(r.target_id || "").slice(0, 8)}</TableCell>
                  <TableCell className="text-right">
                    {r.ok ? (
                      <Badge className="rounded-full bg-emerald-100 text-emerald-800 border-emerald-200 gap-1"><CheckCircle2 className="w-3 h-3" /> Sent</Badge>
                    ) : (
                      <Badge className="rounded-full bg-rose-100 text-rose-800 border-rose-200 gap-1"><XCircle className="w-3 h-3" /> Failed</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
